import { useMemo, useState } from "react";
import { toast } from "sonner";
import { Link, useNavigate } from "@tanstack/react-router";
import { ArrowLeft, Activity, Loader2, RefreshCw, Trash2, Webhook } from "lucide-react";
import { Button } from "@/frontend/components/ui/button";
import { Input } from "@/frontend/components/ui/input";
import { Separator } from "@/frontend/components/ui/separator";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/frontend/components/ui/tooltip";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/frontend/components/ui/dialog";
import { cn } from "@/frontend/lib/utils";
import { formatDateTime } from "@/frontend/utils/shared";
import { CopyField } from "./CopyField";
import {
  useWebhooksQuery,
  useWebhookEventsQuery,
} from "@/frontend/queries/dashboard/settings";
import {
  useUpdateWebhookMutation,
  useDeleteWebhookMutation,
  useRotateWebhookSecretMutation,
  useRedeliverWebhookEventMutation,
} from "@/frontend/mutations/dashboard/settings";
import {
  SEARCH_TO_LIST,
  STATUS_TABS,
} from "@/frontend/types/dashboard/settings/webhooks";

/**
 * Detail view for a single webhook endpoint.
 *
 * 1. Reads the endpoint from the cached webhooks list
 * 2. Renders a header with the URL, status, and enable/rotate/delete actions
 * 3. Lists recent deliveries (webhook events) with status filter tabs
 * 4. Allows manual redelivery of an individual event
 *
 * @param webhookId - The `wh_*` id of the endpoint to display
 */
export function WebhookDetailView({ webhookId }: { webhookId: string }) {
  const navigate = useNavigate();

  const webhooks = useWebhooksQuery();
  const endpoint = (webhooks.data ?? []).find((e) => e.id === webhookId);
  const events = useWebhookEventsQuery(webhookId);

  const [filterStatus, setFilterStatus] = useState<(typeof STATUS_TABS)[number]["key"]>("all");
  const [rotatedSecret, setRotatedSecret] = useState<string | null>(null);
  const [showRotate, setShowRotate] = useState(false);

  const updateWebhook = useUpdateWebhookMutation();
  const deleteWebhook = useDeleteWebhookMutation(() =>
    navigate({ to: "/dashboard/settings", search: SEARCH_TO_LIST }),
  );
  const rotateSecret = useRotateWebhookSecretMutation();
  const redeliver = useRedeliverWebhookEventMutation(webhookId);

  const allEvents = events.data ?? [];
  const filteredEvents = useMemo(
    () =>
      allEvents.filter((ev) => {
        if (filterStatus === "delivered") return ev.delivered_at !== null;
        if (filterStatus === "pending") return ev.delivered_at === null;
        return true;
      }),
    [allEvents, filterStatus],
  );
  const statusCounts = useMemo(
    () => ({
      all: allEvents.length,
      delivered: allEvents.filter((ev) => ev.delivered_at !== null).length,
      pending: allEvents.filter((ev) => ev.delivered_at === null).length,
    }),
    [allEvents],
  );

  if (webhooks.isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="size-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!endpoint) {
    return (
      <div className="flex flex-col gap-4">
        <Link
          to="/dashboard/settings"
          search={SEARCH_TO_LIST}
          className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors w-fit"
        >
          <ArrowLeft className="size-4" />
          Back to Webhooks
        </Link>
        <p className="text-sm text-muted-foreground">Webhook endpoint not found.</p>
      </div>
    );
  }

  const isActive = endpoint.status === "active";

  return (
    <div className="flex flex-col gap-8">
      <Link
        to="/dashboard/settings"
        search={SEARCH_TO_LIST}
        className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors w-fit"
      >
        <ArrowLeft className="size-4" />
        Back to Webhooks
      </Link>

      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-4 min-w-0">
          <Webhook className="size-8 text-muted-foreground shrink-0" />
          <div className="min-w-0">
            <div className="flex items-center gap-2 min-w-0">
              <h2
                className={cn(
                  "text-xl font-semibold truncate min-w-0",
                  !endpoint.description && "font-mono",
                )}
              >
                {endpoint.description || endpoint.url}
              </h2>
              <span
                className={cn(
                  "inline-flex items-center text-xs px-2 py-0.5 rounded-full font-medium capitalize shrink-0",
                  isActive
                    ? "bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400"
                    : "bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400",
                )}
              >
                {isActive ? "Active" : "Disabled"}
              </span>
            </div>
            {endpoint.description && (
              <p className="text-sm text-muted-foreground font-mono mt-0.5 truncate">
                {endpoint.url}
              </p>
            )}
            <div className="flex flex-wrap gap-1.5 mt-2">
              {endpoint.events.includes("*") ? (
                <code className="text-[11px] font-mono bg-muted text-muted-foreground px-2 py-0.5 rounded">
                  All events
                </code>
              ) : (
                endpoint.events.map((e) => (
                  <code
                    key={e}
                    className="text-[11px] font-mono bg-muted text-muted-foreground px-2 py-0.5 rounded"
                  >
                    {e}
                  </code>
                ))
              )}
            </div>
          </div>
        </div>
        <TooltipProvider delayDuration={150}>
          <div className="flex gap-1 shrink-0">
            <Button
              size="sm"
              variant="ghost"
              className="h-8 px-3 text-xs"
              disabled={updateWebhook.isPending}
              onClick={() =>
                updateWebhook.mutate({ id: endpoint.id, status: isActive ? "disabled" : "active" })
              }
            >
              {isActive ? "Disable" : "Enable"}
            </Button>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-8 w-8 p-0"
                  onClick={() => { rotateSecret.reset(); setRotatedSecret(null); setShowRotate(true); }}
                >
                  <RefreshCw className="size-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Rotate signing secret</TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-8 w-8 p-0 text-destructive hover:text-destructive hover:bg-destructive/10"
                  disabled={deleteWebhook.isPending}
                  onClick={() => {
                    if (confirm("Delete this endpoint? Its delivery history will be removed.")) {
                      deleteWebhook.mutate({ id: endpoint.id });
                    }
                  }}
                >
                  <Trash2 className="size-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>Delete endpoint</TooltipContent>
            </Tooltip>
          </div>
        </TooltipProvider>
      </div>

      <Separator />

      {/* Deliveries */}
      <div>
        <h3 className="text-sm font-semibold mb-5">Recent Deliveries</h3>

        <div className="flex gap-1 mb-6">
          {STATUS_TABS.map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setFilterStatus(key)}
              className={cn(
                "px-2 py-1 text-xs font-medium rounded transition-colors",
                filterStatus === key
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:bg-muted",
              )}
            >
              {label} ({statusCounts[key]})
            </button>
          ))}
        </div>

        {events.isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="size-6 animate-spin text-muted-foreground" />
          </div>
        ) : filteredEvents.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
            <Activity className="h-8 w-8 mb-2 opacity-50" />
            <p className="text-sm">No deliveries yet</p>
          </div>
        ) : (
          <div className="rounded-lg border divide-y">
            {filteredEvents.map((ev) => {
              const delivered = ev.delivered_at !== null;
              return (
                <div key={ev.id} className="flex items-center gap-4 px-4 py-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <code className="text-xs font-mono">{ev.type}</code>
                      <span
                        className={cn(
                          "inline-flex items-center text-[10px] px-1.5 py-0.5 rounded font-medium",
                          delivered
                            ? "bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400"
                            : "bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400",
                        )}
                      >
                        {delivered
                          ? "Delivered"
                          : `Pending${ev.last_status_code ? ` (${ev.last_status_code})` : ""}`}
                      </span>
                    </div>
                    <div className="text-[11px] text-muted-foreground mt-0.5">
                      {ev.attempts} attempt{ev.attempts === 1 ? "" : "s"} · {formatDateTime(ev.created_at)}
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 px-2 text-xs shrink-0"
                    disabled={redeliver.isPending}
                    onClick={() =>
                      redeliver.mutate(
                        { id: ev.id },
                        {
                          onSuccess: () => toast.success("Redelivery queued"),
                          onError: (err) => toast.error(err.message),
                        },
                      )
                    }
                  >
                    Redeliver
                  </Button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Rotate secret dialog */}
      <Dialog
        open={showRotate}
        onOpenChange={(open) => { if (!open) { setShowRotate(false); setRotatedSecret(null); rotateSecret.reset(); } }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{rotatedSecret ? "New signing secret" : "Rotate signing secret"}</DialogTitle>
            <DialogDescription>
              {rotatedSecret
                ? "Copy the new secret now. The previous secret stops working immediately."
                : "This generates a new signing secret. The old one stops working as soon as you rotate, so update your endpoint first."}
            </DialogDescription>
          </DialogHeader>
          {rotatedSecret ? (
            <CopyField label="Signing secret" value={rotatedSecret} />
          ) : (
            <div className="flex flex-col gap-1">
              <span className="text-xs text-muted-foreground font-medium">Endpoint</span>
              <Input
                readOnly
                tabIndex={-1}
                value={endpoint.url}
                className="font-mono text-xs bg-muted/50 text-muted-foreground"
              />
            </div>
          )}
          {rotateSecret.isError && (
            <p className="text-sm text-destructive">{rotateSecret.error.message}</p>
          )}
          <DialogFooter>
            {rotatedSecret ? (
              <Button onClick={() => { setShowRotate(false); setRotatedSecret(null); rotateSecret.reset(); }}>
                Done
              </Button>
            ) : (
              <>
                <Button variant="outline" onClick={() => setShowRotate(false)}>Cancel</Button>
                <Button
                  variant="destructive"
                  disabled={rotateSecret.isPending}
                  onClick={() =>
                    rotateSecret.mutate(
                      { id: endpoint.id },
                      { onSuccess: (res) => setRotatedSecret(res.secret) },
                    )
                  }
                >
                  {rotateSecret.isPending ? (
                    <>
                      <Loader2 className="size-4 mr-2 animate-spin" />
                      Rotating…
                    </>
                  ) : (
                    "Rotate secret"
                  )}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}